<template>
  <div>
    <div
      v-if="devEnvName"
      class="_dev-env-indicator"
    >
      {{ devEnvName }}
      <template v-if="devEnvIp">
        &middot; {{ devEnvIp }}
      </template>
    </div>
    <browser-not-supported v-if="browserNotSupported" />
    <div
      v-else
      :class="['app', $store.state.breakpoint.smAndDown ? '-mobile' : '']"
    >
      <m-content
        v-if="appMode.includes(appModes.requiresMinorUpdate)"
        :padding-y="6"
        :padding-x="9"
        class="_version-note"
        @click="reload"
      >
        <div class="_icon">
          <m-icon type="start" />
        </div>
        <div class="_text">
          {{ $t('app.newVersion') }}
        </div>
      </m-content>
      <m-content
        v-if="appMode.includes(appModes.foreignLogin)"
        :padding-y="3"
        :padding-x="9"
        class="_foreign-login"
      >
        <div class="_icon">
          <m-icon type="warning" />
        </div>
        <div class="_text">
          {{ $t('app.foreignLogin') }}
        </div>
      </m-content>
      <full-screen-spinner v-if="appMode.includes(appModes.loading)" />
      <template v-if="appMode.includes(appModes.login)">
        <plain-layout>
          <info-page
            v-if="isPublishedView"
            :heading="`🧐 ${$t('publishedView.notPublished.title')}`"
            :text="`${$t('publishedView.notPublished.description')}`"
          />
          <login
            v-else-if="isBrowser"
            @session-established="initializeUser"
          />
          <m-s-login
            v-if="isMsTeams"
            @session-established="initializeUser"
          />
        </plain-layout>
      </template>
      <maintenance v-if="appMode.includes(appModes.maintenance)" />
      <template v-if="appMode.includes(appModes.app)">
        <plain-layout
          v-if="$route.meta.layout === plain"
          :class="['_plain', isMsTeams ? '-ms-teams' : '']"
        >
          <gated-router-view />
        </plain-layout>
        <main-layout v-else-if="authenticatedRoute && isExpired">
          <info-page
            :heading="$t('app.accountExpired.heading')"
            :text="$t('app.accountExpired.explanation')"
          />
        </main-layout>
        <main-layout v-else-if="authenticatedRoute && isDeactivatedAccount">
          <info-page
            :heading="$t('app.accountInactive.heading')"
            :text="$t('app.accountInactive.explanation')"
          />
        </main-layout>
        <main-layout v-else-if="authenticatedRoute && !hasModuleFlags">
          <info-page
            :heading="$t('app.featureNotAvailable.heading')"
            :text="$t('app.featureNotAvailable.explanation')"
            :button-text="$t('app.featureNotAvailable.button')"
          />
        </main-layout>
        <main-layout v-else-if="authenticatedRoute && !hasAccess">
          <info-page
            :heading="$t('app.noAccess.heading')"
            :text="$t('app.noAccess.explanation')"
            :button-text="$t('app.noAccess.button')"
          />
        </main-layout>
        <main-layout v-else-if="$route.meta.layout === main">
          <gated-router-view />
        </main-layout>
        <settings-layout v-else-if="$route.meta.layout === settings">
          <gated-router-view />
        </settings-layout>
        <m-dialog
          :value="showTrialExpired && !trialExpiredDiscarded"
          hide-footer
          center
          max-width="40rem"
          @close="trialExpiredDiscarded = true"
        >
          <upgrade-form
            :title="$t('upgradeForm.subscriptionExpired')"
            expired
            :description="$t('upgradeForm.subscriptionExpiredText')"
            @close="trialExpiredDiscarded = true"
          />
        </m-dialog>
        <goal-details-dialog />
        <template v-if="authenticatedRoute">
          <support-form-dialog />
          <sales-form-dialog />
          <release-notes-dialog v-if="!isPrint" />
          <company-info-dialog />
          <product-survey-dialog
            v-if="false && loggedInUser.productSurveyStatus === null && !isTrial && $route.name !== onboarding && !foreignLogin"
            :user="loggedInUser"
          />
          <resource-center-drawer />
          <invite-users-dialog />
          <loom-video-dialog />
        </template>
        <m-dialog
          :value="appMode.includes(appModes.requiresMajorUpdate)"
          keep-open-on-mask-click
          hide-footer
          max-width="40rem"
          center
        >
          <div class="_version-update">
            <div class="_emoji">
              🌟
            </div>
            <div class="_text">
              {{ $t('app.newVersion') }}
            </div>
            <div class="_action">
              <m-btn
                color="primary"
                block
                @click="reload"
              >
                {{ $t('app.reload') }}
              </m-btn>
            </div>
          </div>
        </m-dialog>
      </template>
      <teleport to="#confirm">
        <m-confirm
          ref="confirm"
          :event-bus="EventBus"
        />
      </teleport>
      <m-overlay-spinner
        :event-bus="EventBus"
      />
      <teleport to="#snackbar">
        <m-snackbar
          :event-bus="EventBus"
        />
      </teleport>
      <m-feature-gate
        :event-bus="EventBus"
      />
      <slide-show />
    </div>
  </div>
</template>

<script>
import BrowserNotSupported from '@/components/BrowserNotSupported.vue';
import CompanyInfoDialog from '@/components/navigation/CompanyInfoDialog.vue';
import FullScreenSpinner from 'shared/components/FullScreenSpinner.vue';
import GatedRouterView from '@/components/layout/GatedRouterView.vue';
import GoalDetailsDialog from '@/components/goal/GoalDetailsDialog.vue';
import InfoPage from '@/components/InfoPage.vue';
import InviteUsersDialog from '@/components/navigation/InviteUsersDialog.vue';
import Login from '@/views/Login.vue';
import LoomVideoDialog from '@/components/LoomVideoDialog.vue';
import MOverlaySpinner from 'shared/components/base/MOverlaySpinner.vue';
import MSLogin from '@/views/MSLogin.vue';
import MainLayout from '@/components/layout/MainLayout.vue';
import Maintenance from '@/components/Maintenance.vue';
import PlainLayout from '@/components/layout/PlainLayout.vue';
import ProductSurveyDialog from '@/components/navigation/ProductSurveyDialog.vue';
import ReleaseNotesDialog from '@/release-notes/ReleaseNotesDialog.vue';
import ResourceCenterDrawer from '@/components/navigation/ResourceCenterDrawer.vue';
import SalesFormDialog from '@/components/plans/SalesFormDialog.vue';
import SettingsLayout from '@/components/layout/SettingsLayout.vue';
import SlideShow from '@/components/onboarding/SlideShow.vue';
import SupportFormDialog from '@/components/plans/SupportFormDialog.vue';
import UpgradeForm from '@/components/plans/UpgradeForm.vue';
import useAccess from '@/composables/access/access';
import useAppMode from '@/composables/app-mode';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useLogin from '@/composables/user/login';
import useMSTeams from '@/composables/msteams';
import usePageVisits from '@/composables/page-visits';
import usePostDenormalizeMiddlewares from '@/store/post-denormalize-middlewares';
import usePrintMode from '@/composables/export/print-mode';
import usePublishedViewLogin from '@/composables/published-view/login';
import useRouteHealer from '@/composables/route-healer';
import useSideBar from '@/composables/logged-in-user/side-bar';
import { DateTime } from 'luxon';
import { EventBus } from '@/lib/event-bus';
import { MAIN_LAYOUT, PLAIN, SETTINGS_LAYOUT } from '@/lib/layout';
import { SIDEBAR_SERVICE, APP_MODES as appModes } from '@/lib/constants';
import { accountStatus, productPlan, routeName } from 'shared/constants.json';
import { changeLocale } from '@/lang';
import { computed, nextTick, provide, ref, watch } from 'vue';
import { environmentVariable, resolveEnvironmentVariable } from '@/lib/env';
import { logCatch } from '@/lib/logger/logger';
import { mapActions, mapState } from 'vuex';

export default {
  name: 'App',
  props: {
    postDenormalizeSvc: {
      type: Object,
      required: true,
    },
  },
  components: {
    GoalDetailsDialog,
    SlideShow,
    LoomVideoDialog,
    InviteUsersDialog,
    ProductSurveyDialog,
    CompanyInfoDialog,
    SalesFormDialog,
    SupportFormDialog,
    ReleaseNotesDialog,
    ResourceCenterDrawer,
    UpgradeForm,
    SettingsLayout,
    FullScreenSpinner,
    MainLayout,
    PlainLayout,
    BrowserNotSupported,
    Login,
    MSLogin,
    Maintenance,
    InfoPage,
    MOverlaySpinner,
    GatedRouterView,
  },
  setup(props) {
    useRouteHealer();
    const { accountHasFeature, userHasRight } = useAccess();
    const pageVisitsService = usePageVisits();
    const { initializeUser, loading: loginUserLoading } = useLogin(pageVisitsService);
    const { attemptLogin: loginPublishedView, initialize: initializePublishedView, loading: loginPublishedViewLoading, isPublishedView } = usePublishedViewLogin();
    const { attemptLogin, initializeMSTeams, isBrowser, isMsTeams } = useMSTeams();
    const { isPrint } = usePrintMode();

    const initialLoading = ref(true);
    watch(loginUserLoading, (newVal, oldVal) => {
      if (oldVal === true && newVal === false) {
        initialLoading.value = false;
      }
    });
    watch(loginPublishedViewLoading, (newVal, oldVal) => {
      if (oldVal === true && newVal === false) {
        initialLoading.value = false;
      }
    });
    const loading = computed(() => initialLoading.value || loginUserLoading.value || loginPublishedViewLoading.value);
    const { appMode } = useAppMode(loading);
    const { loggedInUser, userLang } = useLoggedInUser();
    const { loggedInUserAccount } = useLoggedInUserAccount();

    const sidebarSvc = useSideBar(loggedInUser);
    provide(SIDEBAR_SERVICE, sidebarSvc);

    usePostDenormalizeMiddlewares(props.postDenormalizeSvc, isPublishedView.value);

    const devEnvName = resolveEnvironmentVariable(environmentVariable.DEV_ENV_NAME) || '';
    const devEnvIp = resolveEnvironmentVariable(environmentVariable.DEV_ENV_IP) || '';

    return {
      devEnvName,
      devEnvIp,
      createPageVisit: pageVisitsService.createPageVisit,
      accountHasFeature,
      userHasRight,
      initializeUser,
      loginPublishedView,
      initializePublishedView,
      initialLoading,
      loading,
      loggedInUser,
      account: loggedInUserAccount,
      userLang,

      isBrowser,
      isMsTeams,
      isPublishedView,
      attemptLogin,
      initializeMSTeams,

      appMode,
      appModes,
      isPrint,
    };
  },
  data() {
    return {
      plain: PLAIN,
      main: MAIN_LAYOUT,
      settings: SETTINGS_LAYOUT,
      EventBus,
      msTeamsContext: null,
      trialExpiredDiscarded: false,
      onboarding: routeName.onboarding,
    };
  },
  computed: {
    ...mapState({
      foreignLogin: (state) => state.foreignLogin,
      isLoggedIn: (state) => state.isLoggedIn,
    }),
    browserNotSupported() {
      return typeof [].includes === 'undefined';
    },
    isExpired() {
      if (this.account.accountSettings.expiresAt === null) {
        return false;
      }
      return DateTime.fromISO(this.account.accountSettings.expiresAt).diff(DateTime.local()).milliseconds < 0;
    },
    showTrialExpired() {
      if (this.account === null || this.account.accountSettings === null) {
        return false;
      }
      return this.account.accountSettings.planId === productPlan.trial && this.isExpired;
    },
    isTrial() {
      return this.account.accountSettings.planId === productPlan.trial;
    },
    isDeactivatedAccount() {
      return this.account.accountSettings.status === accountStatus.paused;
    },
    hasAccess() {
      return this.hasAccessGroupFlags && this.hasModuleFlags;
    },
    hasAccessGroupFlags() {
      if (typeof this.$route.meta.accessGroupFlags === 'undefined') {
        return true;
      }
      return this.userHasRight(this.$route.meta.accessGroupFlags);
    },
    hasModuleFlags() {
      if (typeof this.$route.meta.flags === 'undefined') {
        return true;
      }
      return this.accountHasFeature(this.$route.meta.flags);
    },
    authenticatedRoute() {
      return !this.$route.meta.noAuth;
    },
    publicRoute() {
      return !!this.$route.meta.public;
    },
  },
  methods: {
    ...mapActions({ logoutRoutine: 'logout' }),
    reload() {
      window.location.reload();
    },
    logout() {
      this.$store.commit('USER_NOT_LOGGED_IN');
      nextTick(() => {
        this.logoutRoutine().then(() => {
          this.$router.replace('/');
        });
      });
    },
  },
  watch: {
    appMode(val) {
      if (this.publicRoute && (val.includes(appModes.requiresMajorUpdate) || val.includes(appModes.requiresMinorUpdate))) {
        window.location.href = `${window.location.pathname}?refresh=${new Date().getTime()}`;
      }
    },
    loggedInUser(val) {
      if (val !== null) {
        changeLocale(this.userLang, this);
      }
    },
    $route(val) {
      if (this.loggedInUser === null || this.loggedInUser.uid === undefined || this.foreignLogin) {
        return;
      }

      if (!this.isLoggedIn) {
        return;
      }

      if (this.loading) {
        return;
      }

      this.createPageVisit({
        user: { uid: this.loggedInUser.uid },
        routeName: val.name,
        params: val.params,
        query: val.query,
      });
    },
  },
  created() {
    EventBus.$on('logout', () => this.logout());

    if ([routeName.teamsAuth, routeName.oauthRedirect].includes(this.$route.name)) {
      this.initialLoading = false;
      return;
    }

    if (this.isPublishedView) {
      this.loginPublishedView().then(() => this.initializePublishedView()).catch(logCatch(() => {
        this.initialLoading = false;
      }));
      return;
    }

    const userInitializeResponse = this.initializeUser();
    const initializeTeamResponse = this.initializeMSTeams().catch(() => {});

    userInitializeResponse.then((user) => {
      if (user !== null) {
        return;
      }

      initializeTeamResponse.then((context) => {
        if (context === undefined || context === null) {
          return;
        }

        this.attemptLogin(context).then(() => {
          this.initializeUser();
        });
      });
    });
  },
};
</script>

<style lang="scss" type="text/scss">
  @import "assets/scss/index";

  ._dev-env-indicator {
    position: fixed;
    top: .4rem;
    right: .4rem;
    z-index: 10000;
    padding: .15rem .5rem;
    font-family: monospace;
    font-size: 1.1rem;
    color: rgba(255, 255, 255, .9);
    pointer-events: none;
    background-color: rgba(0, 0, 0, .45);
    border-radius: .3rem;
  }

  #app {
    height: 100%;

    ._version-note {
      position: fixed;
      bottom: 0;
      z-index: 999;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100vw;
      color: white;
      cursor: pointer;
      background-color: $primary-color;

      ._icon {
        margin-right: 1rem;
        margin-bottom: .1rem;
      }
    }

    ._foreign-login {
      position: fixed;
      bottom: 0;
      z-index: 999;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100vw;
      color: map_get($yellow, 'darken-4');
      background-color: $warning-color;

      ._icon {
        margin-right: 1rem;
        margin-bottom: .1rem;
      }
    }

    ._plain {
      &.-ms-teams {
        .scroll-container {
          @media (min-width: $screen-size-md) {
            overflow-x: hidden;
          }
        }
      }

    }
  }

  ._version-update {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    ._emoji {
      margin-bottom: 1rem;
      font-size: 7rem;
    }

    ._text {
      margin-bottom: 2rem;
      font-size: $font-size-6;
      text-align: center;
    }

    ._action {
      width: 100%;
      margin-bottom: 1rem;
    }
  }
</style>
